// LLM brand logo imports (as URL strings for <img> usage)
import openaiLogo from '@/assets/logos/llm/openai.svg'
import claudeLogo from '@/assets/logos/llm/claude.svg'
import perplexityLogo from '@/assets/logos/llm/perplexity.svg'
import geminiLogo from '@/assets/logos/llm/gemini.svg'
import copilotLogo from '@/assets/logos/llm/copilot.svg'
import grokLogo from '@/assets/logos/llm/grok.svg'
import deepseekLogo from '@/assets/logos/llm/deepseek.svg'
import mistralLogo from '@/assets/logos/llm/mistral.svg'
import youcomLogo from '@/assets/logos/llm/youcom.svg'
import phindLogo from '@/assets/logos/llm/phind.svg'
import poeLogo from '@/assets/logos/llm/poe.svg'
import characterLogo from '@/assets/logos/llm/character.svg'

export interface LLMReferralRow {
  name: string
  domain: string
  count: number
  percentage: number
  logo: string | null
  /** Brand color, used only for the fallback dot when a logo is missing */
  color: string
}

// LLM provider configuration with their domains and brand colors
export const LLM_PROVIDERS: Record<string, {
  name: string
  logo: string
  color: string
  domains: string[]
}> = {
  chatgpt: { name: 'ChatGPT', logo: openaiLogo, color: '#10A37F', domains: ['chat.openai.com', 'chatgpt.com', 'openai.com'] },
  claude: { name: 'Claude', logo: claudeLogo, color: '#D97757', domains: ['claude.ai', 'anthropic.com'] },
  perplexity: { name: 'Perplexity', logo: perplexityLogo, color: '#20808D', domains: ['perplexity.ai'] },
  gemini: { name: 'Gemini', logo: geminiLogo, color: '#4285F4', domains: ['gemini.google.com', 'bard.google.com', 'aistudio.google.com'] },
  copilot: { name: 'Copilot', logo: copilotLogo, color: '#FFAC33', domains: ['copilot.microsoft.com', 'bing.com/chat', 'bing.com/copilot'] },
  grok: { name: 'Grok', logo: grokLogo, color: '#1DA1F2', domains: ['grok.x.ai', 'x.com/i/grok'] },
  deepseek: { name: 'DeepSeek', logo: deepseekLogo, color: '#4F46E5', domains: ['chat.deepseek.com', 'deepseek.com'] },
  mistral: { name: 'Mistral', logo: mistralLogo, color: '#FF7000', domains: ['chat.mistral.ai', 'mistral.ai'] },
  youcom: { name: 'You.com', logo: youcomLogo, color: '#6366F1', domains: ['you.com'] },
  phind: { name: 'Phind', logo: phindLogo, color: '#10B981', domains: ['phind.com'] },
  poe: { name: 'Poe', logo: poeLogo, color: '#8B5CF6', domains: ['poe.com'] },
  character: { name: 'Character.AI', logo: characterLogo, color: '#EC4899', domains: ['character.ai', 'beta.character.ai'] },
}

// Detect LLM provider from referrer URL
export const detectLLMProvider = (
  referrer: string,
): { provider: string; providerData: typeof LLM_PROVIDERS[string] } | null => {
  if (!referrer) return null
  const lowerRef = referrer.toLowerCase()
  for (const [key, provider] of Object.entries(LLM_PROVIDERS)) {
    for (const domain of provider.domains) {
      if (lowerRef.includes(domain)) {
        return { provider: key, providerData: provider }
      }
    }
  }
  return null
}

/**
 * AI-assistant referrals from the dashboard payload: referrer rows (not UTM rows)
 * whose domain matches a known assistant, plus any pre-filtered `llmReferrals`.
 * Counts are referral visits, so they add up across assistants.
 */
export function computeLLMReferrals(dashboardData: any): { llmData: LLMReferralRow[]; totalVisits: number } {
  if (!dashboardData?.metrics) {
    return { llmData: [], totalVisits: 0 }
  }

  const llmStats: Record<string, { count: number; domain: string }> = {}
  const add = (referrer: string, count: number) => {
    const detected = detectLLMProvider(referrer)
    if (!detected) return
    const key = detected.provider
    if (!llmStats[key]) llmStats[key] = { count: 0, domain: referrer }
    llmStats[key].count += count
  }

  // referer_group holds the actual referrer domain (bridge no longer pre-categorises)
  const aggregate = dashboardData.metrics.aggregate || []
  aggregate
    .filter((row: any) => row.referer_group_aggregate && !row.utm_medium && row.referer_group)
    .forEach((row: any) => add(row.referer_group, Number(row.count) || 0))

  // Backend may also provide pre-filtered data
  const llmReferrals = dashboardData.metrics.llmReferrals || []
  llmReferrals.forEach((row: any) => add(row.referrer || row.referer_group || row.source, Number(row.count) || 0))

  const total = Object.values(llmStats).reduce((sum, val) => sum + val.count, 0)
  const llmData: LLMReferralRow[] = Object.entries(llmStats)
    .map(([key, stats]) => {
      const provider = LLM_PROVIDERS[key]
      return {
        name: provider?.name || key,
        domain: stats.domain,
        count: stats.count,
        percentage: total > 0 ? (stats.count / total) * 100 : 0,
        logo: provider?.logo || null,
        color: provider?.color || 'var(--chart-2)',
      }
    })
    .sort((a, b) => b.count - a.count)

  return { llmData, totalVisits: total }
}
